import React, { useContext, useState, type FC, type PropsWithChildren } from 'react'; // Added useMemo

import { useSmartlingStrings, withImage } from '@lucid/hooks';
import { keyframes } from '@emotion/react';

import Image from './image';
import Box from '../box/box';
import type { Lucid } from '../../types/types';
import Icon from '../icon/icon';
import layoutContext from '../context/layout-context';

export type ImageCarouselProps = {
  // Optional CSS class for the carousel container.
  className?: string;
  // Array of Directus file objects to display in the upper row of the carousel.
  upperItems: Lucid.Directus.Generated.Directus_Files[];
  // Array of Directus file objects to display in the lower row of the carousel.
  lowerItems: Lucid.Directus.Generated.Directus_Files[];
  // Optional height for the carousel items (number or string).
  height?: number | string;
  // Optional width for each carousel item (number or string).
  itemWidth?: number | string;
  // Optional array of logo strings or a set of localized logos.
  logos?: string[] | Lucid.Logo.LanguagesSet;
};

function getCarouselAnimationCss({
  direction,
  totalWidth,
  shouldPauseAnimation,
  theme,
}: {
  direction: 'right' | 'left';
  totalWidth: number;
  shouldPauseAnimation: boolean;
  theme: any;
}) {
  const isRight = direction === 'right';

  return [
    {
      display: 'flex',
      listStyle: 'none',
      padding: 0,
      margin: 0,
      marginBottom: theme.space.s16,
      animationName: keyframes({
        '0%': {
          transform: isRight ? 'translateX(0%)' : `translateX(-${totalWidth}px)`,
        },
        '100%': {
          transform: isRight ? `translateX(-${totalWidth}px)` : 'translateX(0%)',
        },
      }),
      animationDuration: '300s',
      animationTimingFunction: 'linear',
      animationIterationCount: 'infinite',
    },
    shouldPauseAnimation && {
      animationPlayState: shouldPauseAnimation ? 'paused' : 'running',
    },
    {
      '@media (min-width: 4200px)': {
        animationPlayState: 'paused',
      },
    },
  ];
}

const ImageCarouselItem: FC<{
  item: Lucid.Directus.Generated.Directus_Files;
  height: number | string;
}> = ({ item, height }) => (
  <li
    css={theme => ({
      textAlign: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      margin: '0 8px',
      [theme.breakpoints.bp768.min]: {
        height: height,
      },
    })}
  >
    <Image
      css={{
        display: 'block',
        objectFit: 'contain',
        maxHeight: height || '100%',
        width: 'auto',
        height: height || 'auto',
      }}
      draggable={false}
      imageAlt=""
      imageSrc={withImage(item)}
    />
  </li>
);

// The carousel is designed to not animate on screens wider
//   than 4200px.
const TARGET_WIDTH = 4200;
// The carousel is designed to be 320px in height.
const TARGET_HEIGHT = 320;

const calculateDuplicates = (
  targetWidth: number,
  targetHeight: number,
  items: Lucid.Directus.Generated.Directus_Files[],
): number => {
  if (!items || items.length === 0) return 1;
  // Adjust the width of each image to match the target height
  const adjustedWidths = items?.map(item => ((item.width || 1) / (item.height || 1)) * targetHeight);

  // Calculate the total adjusted width of the array
  const totalAdjustedWidth = adjustedWidths?.reduce((sum, width) => sum + width, 0);

  // Determine how many times the array needs to be duplicated
  return Math.ceil(targetWidth / totalAdjustedWidth);
};

function getTotalWidthAtHeight(
  items: Lucid.Directus.Generated.Directus_Files[],
  targetHeight: number = TARGET_HEIGHT,
): number {
  if (!items || items.length === 0) return 0;

  const totalImageWidth = items.reduce((sum, item) => {
    const width = item.width || 1;
    const height = item.height || 1;
    const scaledWidth = (width / height) * targetHeight;

    return sum + scaledWidth;
  }, 0);

  const totalGapWidth = items.length * 16;

  return totalImageWidth + totalGapWidth;
}

const ImageCarousel: FC<PropsWithChildren<ImageCarouselProps>> = ({
  className,
  upperItems,
  lowerItems,
  height = 320,
  ...rest
}) => {
  const [shouldPauseAnimation, setShouldPauseAnimation] = useState(false);
  const upperDuplicates = calculateDuplicates(TARGET_WIDTH, TARGET_HEIGHT, upperItems);
  const upperTotalWidth = getTotalWidthAtHeight(upperItems, TARGET_HEIGHT);
  const lowerTotalWidth = getTotalWidthAtHeight(lowerItems, TARGET_HEIGHT);
  const repeatedUpperItems = Array.from({ length: upperDuplicates * 2 }, () => upperItems).flat();
  const lowerDuplicates = calculateDuplicates(TARGET_WIDTH, TARGET_HEIGHT, lowerItems);
  const repeatedLowerItems = Array.from({ length: lowerDuplicates * 2 }, () => lowerItems).flat();

  const { shortCode } = useContext(layoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_ct_add_on_pause_animation_label', 'mktg_ct_add_on_play_animation_label'],
    shortCode,
  });

  return (
    <Box
      {...rest}
      className={className}
      css={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        '&:hover': {
          '> div': {
            clip: 'auto',
            clipPath: 'none',
          },
        },
      }}
    >
      {repeatedUpperItems && (
        <Box
          as="ul"
          css={theme =>
            getCarouselAnimationCss({
              direction: 'right',
              totalWidth: upperTotalWidth,
              shouldPauseAnimation,
              theme,
            })
          }
        >
          {repeatedUpperItems.map((item, index) => (
            <ImageCarouselItem
              key={item.id ? `logo-${item?.id}-upper-${index}` : `logo-${item?.filename_disk}-upper-${index}`}
              height={height}
              item={item}
            />
          ))}
        </Box>
      )}
      {repeatedLowerItems && (
        <Box
          as="ul"
          css={theme =>
            getCarouselAnimationCss({
              direction: 'left',
              totalWidth: lowerTotalWidth,
              shouldPauseAnimation,
              theme,
            })
          }
        >
          {repeatedLowerItems.map((item, index) => (
            <ImageCarouselItem
              key={item.id ? `logo-${item.id}-lower-${index}` : `logo-${item.filename_disk}-lower-${index}`}
              height={height}
              item={item}
            />
          ))}
        </Box>
      )}
      <Box
        aria-label={
          shouldPauseAnimation
            ? withSmartling('mktg_ct_add_on_play_animation_label')
            : withSmartling('mktg_ct_add_on_pause_animation_label')
        }
        css={theme => ({
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          position: 'absolute',
          zIndex: 10,
          right: 50,
          bottom: 10,
          backgroundColor: theme.colors.white,
          padding: `${theme.space.s16}px ${theme.space.s24}px`,
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: theme.colors.coolGrey[100],
          borderRadius: theme.radii.r8,
          width: '70px',
          display: 'flex',
          justifyContent: 'center',
          cursor: 'pointer',
          '&:focus, &:focus-visible': {
            clip: 'auto',
            clipPath: 'none',
            outline: '5px solid',
            outlineOffset: 0,
            outlineColor: 'white',
            '&::before': {
              position: 'absolute',
              content: '""',
              top: '-6px',
              bottom: '-6px',
              left: '-6px',
              right: '-6px',
            },
          },
        })}
        onClick={() => setShouldPauseAnimation(!shouldPauseAnimation)}
        onKeyDown={evt => {
          if (evt.key === 'Enter') {
            setShouldPauseAnimation(!shouldPauseAnimation);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <Icon color="coolGrey.100" type={shouldPauseAnimation ? 'Play' : 'Pause'} />
      </Box>
    </Box>
  );
};

export { ImageCarousel };
export default ImageCarousel;
